import { Injectable } from '@angular/core';
import type { PaginationRequiredQueryInterface } from '@zaoblako/shared/common/dtos/pagination-required-query.dto';
import type { DealHelpdeskTaskFormInterface } from '@zaoblako/shared/modules/business/dto/deal-helpdesk-task/deal-helpdesk-task-form.dto';
import type { HelpdeskTaskPageInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-page.dto';
import type { DealHelpdeskTaskQueryInterface } from '@zaoblako/shared/modules/business/dto/deal-helpdesk-task/deal-helpdesk-task-query.dto';
import type { DealProjectFormInterface } from '@zaoblako/shared/modules/business/dto/deal-project/deal-project-form.dto';
import type { ProjectPageInterface } from '@zaoblako/shared/modules/project/dto/project/project-page.dto';
import type { DealProjectQueryInterface } from '@zaoblako/shared/modules/business/dto/deal-project/deal-project-query.dto';
import type { DealRegistryPageInterface } from '@zaoblako/shared/modules/business/dto/deal-registry-page.dto';
import type { DealRegistryQueryInterface } from '@zaoblako/shared/modules/business/dto/deal.registry.query.dto';
import type { DealFileFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-file-find.dto';
import type { DealSearchQueryInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-search-query.dto';
import type { FactMinInterface } from '@zaoblako/shared/common/dtos/fact-min.dto';
import type { DealFlatListQueryInterface } from '@zaoblako/shared/modules/business/dto/deal/deal.flat.list.query.dto';
import type { DealSynchronizeHistoryInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-synchronize-history-find.dto';
import type { DealPageInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-page.dto';
import type { DealsTreeItemInterface } from '@zaoblako/shared/modules/business/dto/deals-tree/deals.tree.item.dto';
import type { DealsTreeSearchQueryInterface } from '@zaoblako/shared/modules/business/dto/deals-tree/deals.tree.search.query.dto';
import type { ReminderFindInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import type { ReminderFormInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder.form.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { DealHistoryPageInterface } from '@zaoblako/shared/modules/business/dto/deal/deal.history.page.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DealService extends BaseService {
  protected baseUrl = '/finance/business/deal';

  public search(queryParams?: DealSearchQueryInterface): Observable<DealPageInterface> {
    return this.http.get<DealPageInterface>(`${this.baseUrl}/search`, {
      params: queryParams ? this.objectToHttpParams(queryParams) : {}
    });
  }

  public hierarchy(queryParams: DealsTreeSearchQueryInterface): Observable<DealsTreeItemInterface[]> {
    return this.http.get<DealsTreeItemInterface[]>(`${this.baseUrl}/tree`, { params: this.objectToHttpParams(queryParams) });
  }

  public flatList(queryParams: DealFlatListQueryInterface): Observable<DealPageInterface> {
    return this.http.get<DealPageInterface>(`${this.baseUrl}/flat`, { params: this.objectToHttpParams(queryParams) });
  }

  public synchronizeAll(): Observable<DealSynchronizeHistoryInterface[]> {
    return this.http.get<DealSynchronizeHistoryInterface[]>(`${this.baseUrl}/synchronize`);
  }

  public getDocuments(dealId: number, queryParams: DealRegistryQueryInterface): Observable<DealRegistryPageInterface> {
    return this.http.get<DealRegistryPageInterface>(`${this.baseUrl}/${dealId}/document`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getHelpdeskTask(dealId: number, queryParams: DealHelpdeskTaskQueryInterface): Observable<HelpdeskTaskPageInterface> {
    return this.http.get<HelpdeskTaskPageInterface>(`${this.baseUrl}/${dealId}/helpdesk-task`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public addHelpdeskTasks(dealId: number, body: DealHelpdeskTaskFormInterface): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${dealId}/helpdesk-task`, body);
  }

  public deleteHelpdeskTasks(dealId: number, body: DealHelpdeskTaskFormInterface): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${dealId}/helpdesk-task`, { body });
  }

  public getProjects(dealId: number, queryParams: DealProjectQueryInterface): Observable<ProjectPageInterface> {
    return this.http.get<ProjectPageInterface>(`${this.baseUrl}/${dealId}/project`, { params: queryParams as any });
  }

  public addProjects(dealId: number, body: DealProjectFormInterface): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${dealId}/project`, body);
  }

  public deleteProjects(dealId: number, body: DealProjectFormInterface): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${dealId}/project`, { body });
  }

  public getMembers(dealId: number): Observable<UserFindInterface[]> {
    return this.http.get<UserFindInterface[]>(`${this.baseUrl}/${dealId}/participants`);
  }

  public getFiles(dealId: number): Observable<DealFileFindInterface[]> {
    return this.http.get<DealFileFindInterface[]>(`${this.baseUrl}/${dealId}/file`);
  }

  public getHistory(dealId: number, params: PaginationRequiredQueryInterface): Observable<DealHistoryPageInterface> {
    return this.http.get<DealHistoryPageInterface>(`${this.baseUrl}/${dealId}/history`, {
      params: this.objectToHttpParams(params)
    });
  }

  public getFactTime(id: number): Observable<FactMinInterface> {
    return this.http.get<FactMinInterface>(`${this.baseUrl}/${id}/fact-time`);
  }

  public createReminder(dealId: number, params: ReminderFormInterface): Observable<ReminderFindInterface> {
    return this.http.post<ReminderFindInterface>(`${this.baseUrl}/${dealId}/reminder`, params);
  }

  public removeReminder(dealId: number): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${dealId}/reminder`);
  }
}
